import React, { useState, useEffect } from "react";
import { useBlogData } from "./useBlogData";
import S from "./Blog.style";
import BlogCard from "@/components/BlogCard";
import LatestBlogCards from "@/components/LatestBlogCards";
import { Link } from "gatsby";
import ArrowButton from "@/components/Buttons/ArrowButton/ArrowButton";
import { useCaseData } from "./useCaseData";
import { ImageDataLike, getImage } from "gatsby-plugin-image";
import { useLatestReportData } from "./useLatestReportData";
import { useLatestArticlesData } from "./useLatestArticlesData";
import { useBuyerData } from "./useBuyerData";
import { useVendorData } from "./useVendorData";
import { useLocalization } from "@/contexts/Localization";

export interface Props {
  typeOfPosts: string;
  numberOfPosts: string | number;
  url: string;
  links: {
    link: {
      url: string;
      title: string;
    }
  }[];
  data: {
    slug: string;
    title: string;
    url: string;
    published_at: string;
    language: string;
    description: string;
    image_url: string;
  }[];
}

type SelectedPostData = {
  category: string;
  date: number;
  image: any;
  imageType: string;
  slug: string;
  language: string;
  text: string;
  title: string;
  url: string;
};

const emptySelectedPost = (): SelectedPostData => ({
  category: "",
  date: 0,
  imageType: "",
  image: "",
  language: "",
  slug: "",
  text: "",
  title: "",
  url: "",
});

const Blog: React.FC<Props> = (props) => {
  const { typeOfPosts, numberOfPosts, url, links } = props;

  const { language, uri } = useLocalization();

  const useData = useBlogData(language);
  const useCase = useCaseData(language);
  const latestReports = useLatestReportData();
  const useLatestArticles = useLatestArticlesData();
  const buyerReports = useBuyerData();
  const vendorReports = useVendorData();

  const [selectedPosts, setSelectedPosts] = useState([emptySelectedPost()]);
  const [postNumber, setPostNumber] = useState(3);

  const selectedData = () => {
    switch (url) {
      case "latest":
        return latestReports;
      case "buyer":
        return buyerReports;
      case "vendor":
        return vendorReports;
      default:
        return latestReports;
    }
  };

  // format api data
  const formattedData = selectedData().map((item) => {
    const imageData = getImage(item.image?.childImageSharp?.gatsbyImageData);
    return {
      category: "Report",
      title: item.title,
      url: item.url,
      date: Number(item.published_at.slice(0, 10).replaceAll("-", "")),
      language: item.language,
      text: item.description,
      imageType: "gatsbyImage",
      image: imageData,
      slug: item.slug,
      fallbackImage: item.image_url,
    };
  }).filter((item) => {
    if (language == 'sv')
      return item.language == 'sv' || item.language == 'en';
    else if (language == 'no')
      return item.language == 'no' || item.language == 'en';
    else
      return item.language == 'en';
  });

  // format Case data
  const formattedCaseData = useCase.map((item: { featuredImage: { node: { gatsbyImage: ImageDataLike | null; }; }; title: any; uri: any; caseInfo: { date: string; excerpt: any; }; slug: any; }) => {
    const imageData = getImage(item.featuredImage?.node.gatsbyImage);
    return {
      category: "Case",
      title: item.title,
      url: item.uri,
      date: Number(item.caseInfo.date.slice(0, 10).replaceAll("-", "")),
      text: item.caseInfo.excerpt,
      imageType: "gatsbyImage",
      image: imageData,
      slug: item.slug,
    };
  });

  // format Article Data 
  const formattedArticleData = useLatestArticles.map((item) => {
    return {
      category: "Article",
      title: item.title,
      url: item.url,
      text: item.preamble,
      date: item.publishedAt.slice(0, 10).replaceAll("-", ""),
      image: item.coverImageUrl,
      fallbackImage: item.coverImageUrl,
    }
  })

  // formate wp data
  const formattedUseData = useData.map((item: { featuredImage: { node: { gatsbyImage: ImageDataLike | null; }; }; title: any; uri: any; date: string; excerpt: any; }) => {
    const imageData = getImage(item.featuredImage?.node.gatsbyImage);
    return {
      category: "Blog",
      title: item.title,
      url: item.uri,
      date: Number(item.date.slice(0, 10).replaceAll("-", "")),
      text: item.excerpt,
      imageType: "gatsbyImage",
      image: imageData,
      slug: item.uri,
    };
  });

  // merge formattedUseData and formattedData
  if (language !== "sv") { formattedArticleData.length = 0; }
  const blogData = [
    ...formattedData,
    ...formattedUseData,
    ...formattedCaseData,
    ...formattedArticleData
  ];

  // sort blogData by date
  const sortedBlogData = blogData.sort((a, b) => {
    return b.date - a.date;
  });
  const [title, setTitle] = useState("Senaste");
  const [button, setButton] = useState({
    title: "Alla insikter",
    url: uri,
    target: "_self",
  });
  const [renderLatestPosts, setRenderLatestPosts] = useState(0);

  // filter posts by typeOfPosts
  useEffect(() => {
    if (typeOfPosts !== "all") {
      const selectPosts = sortedBlogData.filter(
        (post) => typeOfPosts.includes(post.category)
      );
      setSelectedPosts(selectPosts);
    } else {
      setSelectedPosts(sortedBlogData);
    }
  }, [useData]);

  // set postNumber to numberOfPosts
  useEffect(() => {
    if (numberOfPosts === "all") {
      setPostNumber(9);
    } else {
      setPostNumber(Number(numberOfPosts));
    }
  }, [numberOfPosts]);

  const showMorePosts = () => {
    setPostNumber(postNumber + 9);
  };

  const highlightedPosts = selectedPosts.slice(0, 3);

  highlightedPosts.sort((a, b) => b.date - a.date);

  useEffect(() => {
    if (typeOfPosts === "all" || numberOfPosts === "3") {
      setRenderLatestPosts(3);
    } else if (typeOfPosts === 'Blogg') {
      setRenderLatestPosts(3)
    } else {
      setRenderLatestPosts(0);
    }
  }, [typeOfPosts]);

  useEffect(() => {
    if (language == 'sv') {
      if (typeOfPosts === "all") {
        setTitle("Senaste insikter");
        setButton({
          title: "Se alla insikter",
          url: "/insikter/#blog",
          target: "_self",
        });
      } else if (typeOfPosts === "Case") {
        setTitle("Case");
        setButton({
          title: "Se alla case",
          url: "/customer-case",
          target: "_self",
        });
      } else if (typeOfPosts === "Blogg") {
        setTitle("Blogg");
        setButton({
          title: "Se alla blogginlägg",
          url: "/blogg",
          target: "_self",
        });
      } else if (typeOfPosts === "Rapporter") {
        setTitle("Rapporter");
        setButton({
          title: "Se alla rapporter",
          url: "/rapporter",
          target: "_self",
        });
      } else if (typeOfPosts === "Artiklar") {
        setTitle("Artiklar");
        setButton({
          title: "Se alla artiklar",
          url: "/artiklar",
          target: "_self",
        });
      }
    } else if (language == 'no') {
      if (typeOfPosts === "all") {
        setTitle("Siste Innsikter");
        setButton({
          title: "Se alle innsikter",
          url: "/no/datadrevet-innsikt/#blog",
          target: "_self",
        });
      } else if (typeOfPosts === "Case") {
        setTitle("Siste Case");
        setButton({
          title: "Se alle case",
          url: "/no/customer-case",
          target: "_self",
        });
      } else if (typeOfPosts === "Blogg") {
        setTitle("Siste Blogg");
        setButton({
          title: "Se alle bloggposter",
          url: "/no/blogg",
          target: "_self",
        });
      } else if (typeOfPosts === "Rapporter") {
        setTitle("Siste Rapporter");
        setButton({
          title: "Se alle rapporter",
          url: "/no/rapporter",
          target: "_self",
        });
      }
    } else if (language == 'en') {
      if (typeOfPosts === "all") {
        setTitle("Latest Insights");
        setButton({
          title: "View all insights",
          url: "/en/insights/#blog",
          target: "_self",
        });
      } else if (typeOfPosts === "Case") {
        setTitle("Latest Cases");
        setButton({
          title: "View all cases",
          url: "/en/customer-case",
          target: "_self",
        });
      } else if (typeOfPosts === "Blogg") {
        setTitle("Latest Blog posts");
        setButton({
          title: "View all Blog posts",
          url: "/en/blog",
          target: "_self",
        });
      } else if (typeOfPosts === "Rapporter") {
        setTitle("Latest Reports");
        setButton({
          title: "Se all reports",
          url: "/en/reports",
          target: "_self",
        });
      }
    }
  }, [typeOfPosts]);

  // map out posts to BlogCard
  const posts = selectedPosts
    .slice(renderLatestPosts, postNumber)
    .map((post, index) => {
      return <BlogCard key={index} item={post} />;
    });

  // scroll state
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    if (window) {
      const onScroll = () => {
        setScrollPosition(window.scrollY);
      };
      window.addEventListener("scroll", onScroll);
      return () => {
        window.removeEventListener("scroll", onScroll);
      };
    }
    return () => { };
  }, []);

  const noContent = renderLatestPosts > 0 && highlightedPosts.length === 0;
  const message = language === 'en' ? 'Sorry. There doesn\'t seem to be anything here. Try reloading the page or click below to see more content.' : 'Oj. Här vart det tomt. Var vänlig och ladda om sidan eller använd länken nedan för att se mer content.';

  return (
    <S.BlogWrapper>
      <a className="anchor" id="blog" alt={typeOfPosts} />
      <div className="main-inner">
        {numberOfPosts === "all" ? (
          <>
            <S.UlWrapper>
              <S.Ul>
                {links && links.map((item, index) => {
                  return (
                    <Link
                      to={item.link?.url}
                      activeStyle={{ borderBottom: "2px solid #224C5D" }}
                      state={{ scrollPosition: scrollPosition }}
                    >
                      {item.link?.title}
                    </Link>
                  );
                })
                }
              </S.Ul>
            </S.UlWrapper>
          </>
        ) : (
          <></>
        )}
        {renderLatestPosts > 0 && (
          <S.Title>
            {title}
            <S.Line />
          </S.Title>
        )}
        {renderLatestPosts > 0 && (
          <S.LatestContainer>
            <LatestBlogCards array={highlightedPosts} />
          </S.LatestContainer>
        )}
        <S.Container>{posts}</S.Container>
        {numberOfPosts !== "all" && (
          <S.ButtonWrapper>
            <ArrowButton button={button} />
          </S.ButtonWrapper>
        )}
        {selectedPosts.length > 0 &&
          numberOfPosts === "all" &&
          sortedBlogData.length > postNumber && (
            <>
              <S.ReadMoreButton onClick={showMorePosts}>
                {language == 'sv' &&
                  <p>Ladda fler</p>
                }
                {language == 'no' &&
                  <p>Last fler</p>
                }
                {language == 'en' &&
                  <p>Load more</p>
                }
              </S.ReadMoreButton>
            </>
          )}
        {noContent &&
          <S.NoContentMessage>{message}</S.NoContentMessage>
        }
        {noContent &&
          <a style={{ textDecoration: 'none' }} href={language === 'en' ? "https://radargrp.com/en/insights" : "https://radargrp.com/insikter"}>
            <S.ReadMoreButton onClick={() => { }}>{language === 'en' ? 'See all insights' : 'Se alla insikter'}</S.ReadMoreButton>
          </a>
        }
      </div>
    </S.BlogWrapper>
  );
};

export default Blog;
